import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { DelayedAttendanceReportService } from 'src/app/services/delayed-attendance-report.service';
import { MatDialog } from '@angular/material/dialog';
import {
  DelayedReportAdvancedFilters,
  DelayedReportFiltersModalComponent,
} from './modals/delayed-report-filters/delayed-report-filters.modal';

@Component({
  selector: 'app-delayed-attendance-report',
  templateUrl: './delayed-attendance-report.component.html',
  styleUrls: ['./delayed-attendance-report.component.scss'],
})
export class DelayedAttendanceReportComponent implements OnInit {
  isLoading: boolean = false;
  isExporting: boolean = false;
  filtersApplied: boolean = false;

  dataSource: MatTableDataSource<DelayedAttendanceReportRow> = new MatTableDataSource<DelayedAttendanceReportRow>([]);
  displayedColumns: string[] = [
    'patientId',
    'patientName',
    'clientEmail',
    'professional',
    'dueDate',
    'delayText',
  ];

  totalRows: number = 0;
  pageIndex: number = 0;
  pageSize: number = 10;

  filtersForm: FormGroup;
  private readonly DEFAULT_MIN_DELAY_HOURS: number = 72;
  private advancedFilter: DelayedReportAdvancedFilters | null = null;

  constructor(
    private readonly delayedAttendanceReportService: DelayedAttendanceReportService,
    private readonly formBuilder: FormBuilder,
    private readonly materialDialog: MatDialog
  ) {
    this.filtersForm = this.formBuilder.group({
      search: [''],
    });
  }

  ngOnInit(): void {
    this.onSearch();
  }

  onSearch(event?: PageEvent): void {
    this.isLoading = true;

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    } else {
      this.pageIndex = 0;
    }

    const searchTerm: string = (this.filtersForm.value.search || '').trim();
    const queryFilter: any = {};

    if (searchTerm) {
      queryFilter.q = searchTerm;
    }
    queryFilter.minDelay = this.getEffectiveMinimumDelayHours();

    this.delayedAttendanceReportService
      .search({
        filter: queryFilter,
        page: this.pageIndex + 1,
        perPage: this.pageSize,
      })
      .subscribe(
        (dataTable) => {
          this.dataSource.data = [...((dataTable.rows || []) as DelayedAttendanceReportRow[])];
          this.totalRows = dataTable.total || 0;
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  exportCsv(): void {
    this.isExporting = true;

    const searchTerm: string = (this.filtersForm.value.search || '').trim();
    const queryFilter: any = {};

    if (searchTerm) queryFilter.q = searchTerm;
    queryFilter.minDelay = this.getEffectiveMinimumDelayHours();

    this.delayedAttendanceReportService.exportCsv(queryFilter).subscribe(
      (response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Relatorio-Laudos-Atrasados.csv';

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match && match[1]) filename = match[1];
        }

        const blob = new Blob([response.body], {
          type: 'text/csv;charset=utf-8;',
        });
        const blobUrl = window.URL.createObjectURL(blob);

        const anchorElement = document.createElement('a');
        anchorElement.href = blobUrl;
        anchorElement.download = filename;
        document.body.appendChild(anchorElement);
        anchorElement.click();
        anchorElement.remove();

        window.URL.revokeObjectURL(blobUrl);
        this.isExporting = false;
      },
      () => {
        this.isExporting = false;
      }
    );
  }

  applySearch(): void {
    if (this.filtersForm.invalid) return;
    this.onSearch();
  }

  clearFilters(): void {
    this.advancedFilter = null;
    this.filtersApplied = false;
    this.onSearch();
  }

  clearSearch(): void {
    this.filtersForm.patchValue({ search: '' });
    this.onSearch();
  }

  formatDate(value?: string): string {
    if (!value) return '-';
    const d = new Date(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  }

  formatDelayText(value?: string): string {
    if (!value) return '-';
    const daysMatch = value.match(/(\d+)\s*dias?/i);
    const hoursMatch = value.match(/(\d+)\s*horas?/i);
    if (!daysMatch && !hoursMatch) return value;

    const days = daysMatch ? Number(daysMatch[1]) : 0;
    const hours = hoursMatch ? Number(hoursMatch[1]) : 0;

    const dayLabel = days === 1 || days === 0 ? 'dia' : 'dias';
    const hourLabel = hours === 1 || hours === 0 ? 'hora' : 'horas';

    return `${days} ${dayLabel} e ${hours} ${hourLabel}`;
  }

  getEffectiveMinimumDelayHours(): number {
    const advancedMin = this.advancedFilter?.minDelay;
    if (advancedMin === null || advancedMin === undefined) return this.DEFAULT_MIN_DELAY_HOURS;
    const numericValue = Number(advancedMin);
    if (Number.isNaN(numericValue)) return this.DEFAULT_MIN_DELAY_HOURS;
    return Math.max(0, Math.min(1000, numericValue));
  }

  openFiltersModal(): void {
    const dialogRef = this.materialDialog.open(DelayedReportFiltersModalComponent, {
      width: '560px',
      maxHeight: '90vh',
      height: 'auto',
      panelClass: 'delayed-report-filters-dialog',
      autoFocus: false,
      restoreFocus: false,
      data: { initial: this.advancedFilter, defaultMin: this.DEFAULT_MIN_DELAY_HOURS },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: DelayedReportAdvancedFilters | 'close' | undefined) => {
      if (!result || result === 'close') return;
      const value = result?.minDelay;
      const normalized = (value === null || value === undefined)
        ? this.DEFAULT_MIN_DELAY_HOURS
        : Math.max(0, Math.min(1000, Number(value)));
      const hasAny = normalized !== this.DEFAULT_MIN_DELAY_HOURS;
      this.advancedFilter = hasAny ? { minDelay: normalized } : null;
      this.filtersApplied = hasAny;
      this.onSearch();
    });
  }

  onFiltersClick(): void {
    if (this.filtersApplied) {
      this.clearFilters();
    } else {
      this.openFiltersModal();
    }
  }
}

export interface DelayedAttendanceReportRow {
  patientId: number;
  patientName: string;
  clientEmail: string;
  professional: string;
  dueDate: string;
  delayText: string;
}
